import { Flex, Switch, Text, Title } from '@mantine/core';
import dayjs from 'dayjs';
import { useCompanyConfirmDeactivate } from '@features/company';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateActivityCompany, type Company } from '@entities/company';
import { QueryKey } from '@shared/config';
import { showNotification } from '@shared/utils';

type CompanyHeaderProps = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
};

export const CompanyHeader = ({
  id,
  name,
  createdAt,
  updatedAt,
  isActive,
}: CompanyHeaderProps) => {
  const queryClient = useQueryClient();

  const { mutate: updateActivityCompany, isPending } = useUpdateActivityCompany(
    id.toString()
  );

  const { openCompanyConfirmDeactivate } = useCompanyConfirmDeactivate();

  const handleChangeActivity = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked === false) {
      openCompanyConfirmDeactivate();

      return;
    }

    updateActivityCompany(
      { isActive: true },
      {
        onSuccess: data => {
          queryClient.setQueryData(
            [QueryKey.GET_COMPANY, id?.toString()],
            (company: Company) => ({ ...company, ...data })
          );
        },
        onError: error => {
          showNotification({
            error,
            variant: 'error',
            message:
              error.response?.data.message ||
              'Не удалось активировать компанию',
          });
        },
      }
    );
  };

  return (
    <Flex w='100%' direction='column' gap={32}>
      <Flex wrap='wrap' rowGap={16} columnGap={32} align='center'>
        <Flex gap={8}>
          <Text size='sm' c='tertiary.7'>
            ID:
          </Text>
          <Text size='sm' c='primary.7'>
            {id}
          </Text>
        </Flex>

        <Flex gap={8}>
          <Text size='sm' c='tertiary.7'>
            Дата создания:
          </Text>
          <Text size='sm' c='primary.7'>
            {dayjs(createdAt).format('DD.MM.YYYY, HH:mm')}
          </Text>
        </Flex>

        <Flex gap={8}>
          <Text size='sm' c='tertiary.7'>
            Дата изменения:
          </Text>
          <Text size='sm' c='primary.7'>
            {dayjs(updatedAt).format('DD.MM.YYYY, HH:mm')}
          </Text>
        </Flex>

        <Flex gap={12} align='center'>
          <Text size='sm'>Активность:</Text>
          <Switch
            checked={isActive}
            onChange={event => handleChangeActivity(event)}
            label={isActive ? 'Активен' : 'Неактивен'}
            disabled={isPending}
          />
        </Flex>
      </Flex>

      <Title order={3}>{name}</Title>
    </Flex>
  );
};
